import { useEffect, useState } from "react";
import Select from "react-select";
import Swal from "sweetalert2";
import { useParams } from "react-router-dom";
import apiInstance from "../../utils/axios";
import UserData from "../plugins/UserData";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from 'react-i18next';

const Toast = Swal.mixin({
    toast: true,
    position: "top",
    showConfirmButton: false,
    timer: 3000,
    timerProgressBar: true,
});

function ProductNutrients() {
  const [productData, setProductData] = useState(null)
  const [productId, setProductId] = useState(null)
  const [isLoading, setIsLoading] = useState(false);
  const { hashedId } = useParams();
  const { t } = useTranslation();

  const navigate = useNavigate();
  const [productNutrients, setProductNutrients] = useState(null);

    let lang_browser = navigator.language.toLowerCase();
    let lang = localStorage.getItem('language') || (lang_browser.startsWith("en") ? "en" : lang_browser);

  const [formData, setFormData] = useState({
      product: productId,
      total_acid: '',
      total_sugar: '',
      glycerin: '',
      alcohol: '',
      caloric_value_kj: '',
      specific_data_FSPS: false,
      fat: '',
      saturated_fatty_acids: '',
      proteins: '',
      salts: '',
  });

  const validateForm = () => {
    if (!formData.product ||
        !formData.total_acid ||
        !formData.total_sugar ||
        !formData.glycerin ||
        !formData.alcohol ||
        !formData.caloric_value_kj
    ) {
        return false;
    }
    return true;
  };

  useEffect(() => {
    if (hashedId) {
        apiInstance.get(`decode-id/${hashedId}/`).then((res) => {
          setProductId(res.data.id);

          setFormData((prev) => ({
            ...prev,
            product: `${res.data.id}`,
          }));

          apiInstance.get(`/product_nutrients/?product=${res.data.id}`).then((res2) => {
            const data = res2.data;
            if (data.length > 0) {
                setProductNutrients(data[0]);
                setFormData(data[0]);
            }
          }).catch((error) => {
            console.error("Error fetching product nutrients:", error);
          })
        });
    }
  }, [hashedId]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
        ...prev,
        [name]: type === 'checkbox' ? checked : value,
    }));
  };


  useEffect(() => {
    const calculateCaloricValue = () => {
      if (!formData.specific_data_FSPS){
        //  && formData.total_sugar && formData.glycerin && formData.total_acid && formData.alcohol
        // Use a setTimeout to ensure the state has updated
        setTimeout(() => {
          const total_sugar = parseFloat(formData.total_sugar) || 0; // Convert to number, default to 0
          const glycerin = parseFloat(formData.glycerin) || 0;
          const total_acid = parseFloat(formData.total_acid) || 0;
          const alcohol = parseFloat(formData.alcohol) || 0;
  
          const sugar_g_mL = total_sugar / 10;
          const glyc_g_mL = glycerin / 10;
          const acid_g_mL = total_acid / 10;
          const alc_100mL = alcohol * 7.8924 / 10;
  
          const cal_sugar = sugar_g_mL * 17;
          const cal_glyc = glyc_g_mL * 10;
          const cal_acid = acid_g_mL * 13;
          const cal_alc = alc_100mL * 29;
  
          const total_cal_kj = cal_sugar + cal_glyc + cal_acid + cal_alc;
  
          setFormData((prev) => ({
              ...prev,
              caloric_value_kj: total_cal_kj.toFixed(2),
              fat: '',
              salts: '',
              proteins: '',
              saturated_fatty_acids: '',

            }));
        }, 0); // Execute after a very short delay
      } else {
        setFormData((prev) => ({
          ...prev,
          caloric_value_kj: '',
        }));
  
      }
    };

    calculateCaloricValue();
  },[formData.total_sugar, formData.glycerin, formData.total_acid, formData.alcohol, formData.specific_data_FSPS])

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()){
      Toast.fire({
          icon: "error",
          title: "Please fill out all required fields",
      })
      return
    }

    try {
      setIsLoading(true);
      const method = productNutrients ? 'PATCH' : 'POST';
      const url = productNutrients ? `/product_nutrients/${productNutrients.id}/` : '/product_nutrients/'
      

      let response; // Declare response *outside* the if/else blocks

      if (method === 'POST') {
          response = await apiInstance.post(url, formData);
      } else {
          response = await apiInstance.patch(url, formData);
      }

      if (response && response.status === 200 || response.status === 201) {
          setIsLoading(false);
          Toast.fire({
              icon: "success",
              title: "Form submitted successfully",
          });
          navigate(`/product-ingredients/${hashedId}`)
      } else {
        setIsLoading(false);
        if (response) {
          console.error("Server Error:", response.data);
          alert("An error occurred while submitting the form.");
        } else {
          console.error("Unknown Error");
          alert("An unknown error occurred while submitting the form.");
        }
      }
    } catch (error) {
        setIsLoading(false);
        console.error("Fetch Error:", error);
        alert("A network error occurred. Please try again later.");
    }
  };

  return (
    <>
      <div className="container">
        <p className="form-heading-1">{t("productNutrients.headings.title")}</p>
        <p className="desc-text">{t("productNutrients.headings.description")}</p>
        <form className="form-width-1" onSubmit={handleSubmit}>
          <div className="form-input-container-1">
            <label htmlFor="total_acid" className="form-label-1 no-margin">{t("productNutrients.labels.totalAcid")}</label>
            <input
                id="total_acid"
                name="total_acid"
                type="number"
                step="0.1"
                placeholder="0.00"
                className="form-input-2"
                value={formData.total_acid}
                onChange={handleChange}
            />
          </div>
          <div className="form-input-container-1">
            <label htmlFor="total_sugar" className="form-label-1 no-margin">{t("productNutrients.labels.totalSugar")}</label>
            <input
                id="total_sugar"
                name="total_sugar"
                className="form-input-2"
                type="number"
                step="0.1"
                placeholder="0.00"
                value={formData.total_sugar}
                onChange={handleChange}
            />
          </div>
          <div className="form-input-container-1">
            <label htmlFor="glycerin" className="form-label-1 no-margin">{t("productNutrients.labels.glycerin")}</label>
            <input
                id="glycerin"
                name="glycerin"
                type="number"
                step="0.1"
                placeholder="0.00"
                className="form-input-2"
                value={formData.glycerin}
                onChange={handleChange}
            />
          </div>
          <div className="form-input-container-1">
            <label htmlFor="alcohol" className="form-label-1 no-margin">{t("productNutrients.labels.alcohol")}</label>
            <input
                id="alcohol"
                name="alcohol"
                type="number"
                step="0.1"
                placeholder="0.00"
                className="form-input-2"
                value={formData.alcohol}
                onChange={handleChange}
            />
          </div>
          <div className="form-input-container-1">
            <label htmlFor="caloric_value_kj" className="form-label-1 no-margin">{t("productNutrients.labels.caloricValue")}</label>
            <input
                id="caloric_value_kj"
                name="caloric_value_kj"
                step="0.1"
                placeholder="0.00"
                className="form-input-2"
                value={formData.caloric_value_kj}
                onChange={handleChange}
            />
          </div>
          <div className="form-input-container-1">
            <input
                id="specific_data_FSPS"
                name="specific_data_FSPS"
                type="checkbox"
                checked={formData.specific_data_FSPS}
                onChange={handleChange}
                style={{ display: "inline-block", marginRight: "8px" }}
            />
            <label htmlFor="specific_data_FSPS" className="form-label-1 no-margin" style={{ display: "inline-block" }}>{t("productNutrients.labels.specificDataFSPS")}</label>
          </div>
          { formData.specific_data_FSPS && 
            <>
              <div className="form-input-container-1">
                <label htmlFor="fat" className="form-label-1 no-margin">{t("productNutrients.labels.fat")}</label>
                <input
                    id="fat"
                    name="fat"
                    step="0.1"
                    placeholder="0.00"
                    className="form-input-2"
                    value={formData.fat}
                    onChange={handleChange}                    
                />
              </div>
              <div className="form-input-container-1">
              <label htmlFor="saturated_fatty_acids" className="form-label-1 no-margin">{t("productNutrients.labels.saturatedFattyAcids")}</label>
              <input
                  id="saturated_fatty_acids"
                  name="saturated_fatty_acids"
                  step="0.1"
                  placeholder="0.00"
                  className="form-input-2"
                  value={formData.saturated_fatty_acids}
                  onChange={handleChange}
              />
              </div>
              <div className="form-input-container-1">
              <label htmlFor="proteins" className="form-label-1 no-margin">{t("productNutrients.labels.proteins")}</label>
              <input
                  id="proteins"
                  name="proteins"
                  step="0.1"
                  placeholder="0.00"
                  className="form-input-2"
                  value={formData.proteins}
                  onChange={handleChange}
              />
              </div>
              <div className="form-input-container-1">
                <label htmlFor="salts" className="form-label-1 no-margin">{t("productNutrients.labels.salts")}</label>
                <input
                    id="salts"
                    name="salts"
                    step="0.1"
                    placeholder="0.00"
                    className="form-input-2"
                    value={formData.salts}
                    onChange={handleChange}
                />
              </div>
            </>
          }
            <div className="form-input-container-3">    
              {isLoading 
              ? <button disabled className="form-button-submit-2" type="submit">
                  <i className="fas fa-spinner fa-spin"></i>
                  <span className="mr-2">Processing</span>
                  </button>
              : <button className="form-button-submit-2" type="submit"><span className="mr-2">Submit</span>
                  </button>
              }
            </div>
        </form>
      </div>
    </>
  )
}

export default ProductNutrients